import { faThumbsDown, faThumbsUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { AuthenticationStatus } from "~/contexts/authn";
import useAuthN from "~/hooks/use-authn";
import { getLogger } from "~/utils/logging";

const logger = getLogger("recipe-voting-input")

interface VotingProps {
  score: number;
  uservote?: 'UPVOTE' | 'DOWNVOTE';
  upvote: VoidFunction;
  downvote: VoidFunction;
  unvote: VoidFunction;
}

interface LocalState {
  uservote?: 'UPVOTE' | 'DOWNVOTE';
  score: number;
}

const Voting: React.FC<VotingProps> = (props) => {
  const { upvote, downvote, unvote } = props;
  const theme = useTheme();
  // copy props to local state that we can override
  const [localState, setLocalState] = useState<LocalState>(props);
  const authn = useAuthN();
  const router = useRouter();

  useEffect(() => {
    setLocalState(props)
  }, [props])

  const { score, uservote } = localState;

  // todo: number format here
  const scoreString: string = !score && !uservote ? "-" : score.toString();

  // const defaultColor: string = theme ? theme.colors.powderWhite : "grey";
  const defaultColor: string = "lightgrey";
  const selectColor: string = theme ? theme.colors.selectedOrange : defaultColor
  const upColor = uservote === "UPVOTE" ? selectColor : defaultColor;
  const downColor = uservote === "DOWNVOTE" ? selectColor : defaultColor;

  const handleUnauthenticated = () => {
    logger.debug('handling unauthenticated voter')
    router.push(`/sign-in`)
  }

  const handleUnvote = () => {
    logger.debug(`received unvote`)
    if (authn.status !== AuthenticationStatus.LOGGED_IN) {
      handleUnauthenticated()
      return;
    }
    unvote && unvote()
    setLocalState(prior => {
      if (prior.uservote === "DOWNVOTE") {
        return { score: prior.score+1 }
      }
      if (prior.uservote === "UPVOTE") {
        return { score: prior.score-1 }
      }
      return prior
    })
  }

  const handleUpvote = () => {
    if (authn.status !== AuthenticationStatus.LOGGED_IN) {
      handleUnauthenticated()
      return;
    }

    if (localState.uservote === "UPVOTE") {
      handleUnvote()
      return
    }

    logger.debug(`received upvote`)
    upvote && upvote()
    setLocalState(prior => {
      if (prior.uservote === "UPVOTE") {
        return prior
      }
      if (!prior.uservote) {
        return {score: prior.score+1, uservote: 'UPVOTE'}
      }
      if (prior.uservote === "DOWNVOTE") {
        return {score: prior.score+2, uservote: "UPVOTE"}
      }
      return prior
    })
  }

  const handleDownvote = () => {
    if (authn.status !== AuthenticationStatus.LOGGED_IN) {
      handleUnauthenticated()
      return;
    }

    if (localState.uservote === "DOWNVOTE") {
      handleUnvote()
      return
    }

    logger.debug(`received downvote`)
    downvote && downvote()
    setLocalState(prior => {
      if (prior.uservote === "DOWNVOTE") {
        return prior
      }
      if (!prior.uservote) {
        return {score: prior.score-1, uservote: "DOWNVOTE"}
      }
      return {score: prior.score-2, uservote: "DOWNVOTE"}
    })
  }

  return (
    <Container>
      <VoteElement key='up' color={upColor}>
        <div className="upvote" onClick={handleUpvote}>
          <FontAwesomeIcon icon={faThumbsUp} />
        </div>
      </VoteElement>
      <VoteElement key='number' color={"black"}>
        <div className="score">
          {scoreString}
        </div>
      </VoteElement>
      <VoteElement key='down' color={downColor}>
        <div className="downvote" onClick={handleDownvote}>
          <FontAwesomeIcon icon={faThumbsDown} />
        </div>
      </VoteElement>
    </Container>
  )
}

const Container = styled.div`
  position: relative;
  width: 90px;
  height: 30px;
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border: 1px lightgrey solid;
  // border-radius: 25px;
  // background: ${(props) => props.theme.colors.onyx};
  gap: 6px;
`;

const VoteElement = styled.div<{color: string}>`
  position:       relative;
  width:          20px;
  height:         100%;
  color:          ${props => props.color};

  .score {
    height:           100%;
    display:          flex;
    align-items:      center; /* Vertical alignment */
    justify-content:  center; /* Horizontal alignment (optional) */
    font-family:      Arial, Helvetica, sans-serif;
    font-size:        14px;
    font-weight:      700;
  }

  .upvote, .downvote {
    width: 15px;

    :hover {
      color: ${props => props.theme.colors.highlightOrange};
    }
  }

  .upvote {
    position: absolute;
    top: 6px;
    left: 3px;
  }

  .downvote {
    position: absolute;
    top: 8px;
    right: 3px;
  }
`

export default Voting;
